import React from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@ohif/ui-next';

// Route Components
import DataSourceWrapper from './DataSourceWrapper';
import WorkList from './WorkList';
import Local from './Local';
import Debug from './Debug';
import NotFound from './NotFound';
import buildModeRoutes from './buildModeRoutes';
import PrivateRoute from './PrivateRoute';
import PropTypes from 'prop-types';
import { routerBasename } from '../utils/publicUrl';
import { useAppConfig } from '@state';
import { history } from '../utils/history';


import Home from '../pages/Home';

const NotFoundServer = ({
  message = 'Unable to query for studies at this time. Check your data source configuration or network connection',
}) => {
  return (
    <div className="bg-black absolute flex h-full w-full flex-col items-center justify-center">
      <div className="flex flex-col items-center justify-center gap-8">
        <img
          src="/assets/images/logo-desktop.png"
          alt="Clínica Cayetano Heredia"
          className="h-16 w-auto object-contain"
        />
        <div className="text-center max-w-md px-4">
          <h4 className="text-white text-lg mb-2">
            Error de conexión
          </h4>
          <p className="text-gray-400 text-sm">
            {message}
          </p>
        </div>
        <footer className="mt-4 flex items-center justify-center gap-4">
          <span className="text-primary text-xs">2026 Clínica Cayetano Heredia</span>
          <span className="text-gray-600">|</span>
          <a
            href="https://davix.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary text-xs text-gray-400 transition-colors duration-200"
          >
            powered by Davix
          </a>
        </footer>
      </div>
    </div>
  );
};

NotFoundServer.propTypes = {
  message: PropTypes.string,
};

const NotFoundStudy = () => {
  return (
    <div className="bg-black absolute flex h-full w-full flex-col items-center justify-center">
      <div className="flex flex-col items-center justify-center gap-8">
        <img
          src="/assets/images/logo-desktop.png"
          alt="Clínica Cayetano Heredia"
          className="h-16 w-auto object-contain"
        />
        <div className="text-center">
          <h4 className="text-white text-lg mb-2">
            Estudio no disponible
          </h4>
          <p className="text-gray-400 text-sm">
            El estudio solicitado no se encuentra disponible
          </p>
        </div>
        <footer className="mt-4 flex items-center justify-center gap-4">
          <span className="text-primary text-xs"> 2026 Clínica Cayetano Heredia</span>
          <span className="text-gray-600">|</span>
          <a
            href="https://davix.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary text-xs text-gray-400 transition-colors duration-200"
          >
            powered by Davix
          </a>
        </footer>
      </div>
    </div>
  );
};

// TODO: Include "routes" debug route if dev build
const bakedInRoutes = [
  {
    path: `/notfoundserver`,
    children: NotFoundServer,
  },
  {
    path: `/notfoundstudy`,
    children: NotFoundStudy,
  },
  // {
  //   path: `/debug`,
  //   children: Debug,
  // },
  {
    path: `/local`,
    children: Local.bind(null, { modePath: '' }), // navigate to the worklist
  },
  {
    path: `/localbasic`,
    children: Local.bind(null, { modePath: 'viewer/dicomlocal' }),
  },
];

// NOT FOUND (404)
const notFoundRoute = { path: '*', children: NotFound };

const createRoutes = ({
  modes,
  dataSources,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
  showStudyList,
}: withAppTypes) => {
  const routes =
    buildModeRoutes({
      modes,
      dataSources,
      extensionManager,
      servicesManager,
      commandsManager,
      hotkeysManager,
    }) || [];

  const { customizationService } = servicesManager.services;

  const path =
    routerBasename.length > 1 && routerBasename.endsWith('/')
      ? routerBasename.substring(0, routerBasename.length - 1)
      : routerBasename;

  console.log('Registering worklist route', routerBasename, path);

  //lacs change 2026
  // const WorkListRoute = {
  //   path: '/',
  //   children: DataSourceWrapper,
  //   private: true,
  //   props: { children: WorkList, servicesManager, extensionManager },
  // };

  const HomeRoute = {
    path: '/',
    children: Home,
    private: true,
  };

  const customRoutes = customizationService.getCustomization('routes.customRoutes');

  const allRoutes = [
    ...routes,
    HomeRoute, // Always include Home route regardless of showStudyList
    ...(customRoutes?.routes || []),
    ...bakedInRoutes,
    customRoutes?.notFoundRoute || notFoundRoute,
  ];

  function RouteWithErrorBoundary({ route, ...rest }) {
    const [appConfig] = useAppConfig();
    const { showErrorDetails } = appConfig;

    history.navigate = useNavigate();

    // eslint-disable-next-line react/jsx-props-no-spreading
    return (
      <ErrorBoundary
        context={`Route ${route.path}`}
        showErrorDetails={showErrorDetails}
      >
        <route.children
          {...rest}
          {...route.props}
          route={route}
          servicesManager={servicesManager}
          extensionManager={extensionManager}
          hotkeysManager={hotkeysManager}
        />
      </ErrorBoundary>
    );
  }

  const { userAuthenticationService } = servicesManager.services;

  // All routes are private by default and then we let the user auth service
  // to check if it is enabled or not
  // Todo: I think we can remove the second public return below
  return (
    <Routes>
      {allRoutes.map((route, i) => {
        return route.private === true ? (
          <Route
            key={i}
            path={route.path}
            element={
              <PrivateRoute
                handleUnauthenticated={() => userAuthenticationService.handleUnauthenticated()}
              >
                <RouteWithErrorBoundary route={route} />
              </PrivateRoute>
            }
          ></Route>
        ) : (
          <Route
            key={i}
            path={route.path}
            element={<RouteWithErrorBoundary route={route} />}
          />
        );
      })}
    </Routes>
  );
};

export default createRoutes;
